import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { useTour } from "@/contexts/TourContext";
import { LayoutDashboard, Users, FileText, Truck, Workflow, LogOut, Shield, Globe, RotateCcw, Link, Sparkles, Copy, CheckCircle, Eye, Activity, KeyRound } from "lucide-react";
import { analytics } from "@/lib/analytics";
import { BrandLogo } from "@/components/BrandLogo";

const API = `${process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "")}/api`;

// Tabs visible in real-admin mode (full operator control).
const ADMIN_NAV = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/admin/analytics", icon: Activity, label: "Analytics" },
  { to: "/admin/deals", icon: FileText, label: "Deals" },
  { to: "/admin/capital-flows", icon: Workflow, label: "Flow Engine" },
  { to: "/admin/supply-chain", icon: Link, label: "Supply Chain" },
  { to: "/admin/profiles", icon: Shield, label: "Profiles" },
  { to: "/admin/corridor", icon: Globe, label: "Corridor" },
  { to: "/admin/velocity", icon: RotateCcw, label: "Velocity" },
  { to: "/admin/suppliers", icon: Truck, label: "Suppliers" },
  { to: "/admin/applications", icon: Users, label: "Network" },
  { to: "/admin/security", icon: KeyRound, label: "Security" },
];

// Curated tabs for the investor demo (full pitch experience). Hides admin-only
// views (Security, Network/applications, Analytics) but exposes the full
// network value prop including Suppliers — strictly read-only.
const DEMO_NAV_INVESTOR = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/admin/deals", icon: FileText, label: "Deals" },
  { to: "/admin/capital-flows", icon: Workflow, label: "Flow Engine" },
  { to: "/admin/supply-chain", icon: Link, label: "Supply Chain" },
  { to: "/admin/profiles", icon: Shield, label: "Profiles" },
  { to: "/admin/corridor", icon: Globe, label: "Corridor" },
  { to: "/admin/velocity", icon: RotateCcw, label: "Velocity" },
  { to: "/admin/suppliers", icon: Truck, label: "Suppliers" },
];

// BUYER (Importer): focused on supplier KYC, capital deployment to source goods,
// supply-chain visibility. No investor-specific metrics (Velocity, Deals).
const DEMO_NAV_BUYER = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/admin/suppliers", icon: Truck, label: "Suppliers" },
  { to: "/admin/profiles", icon: Shield, label: "Profiles" },
  { to: "/admin/supply-chain", icon: Link, label: "Supply Chain" },
  { to: "/admin/capital-flows", icon: Workflow, label: "Flow Engine" },
];

// EXPORTER (Supplier): focused on faster settlement, buyer KYC, end-to-end
// chain visibility on their own product. No supplier or investor metrics.
const DEMO_NAV_EXPORTER = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/admin/capital-flows", icon: Workflow, label: "Flow Engine" },
  { to: "/admin/profiles", icon: Shield, label: "Profiles" },
  { to: "/admin/supply-chain", icon: Link, label: "Supply Chain" },
];

// DOWNSTREAM BUYER: focused on traceability + corridor of upstream supply.
// No supplier ops queue, no investor metrics, no deal pipeline.
const DEMO_NAV_DOWNSTREAM = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/admin/supply-chain", icon: Link, label: "Supply Chain" },
  { to: "/admin/profiles", icon: Shield, label: "Profiles" },
  { to: "/admin/corridor", icon: Globe, label: "Corridor" },
];

// Routes the investor demo session must NOT see (defence in depth in case
// they paste a URL directly).
const DEMO_BLOCKED_PREFIXES = ["/admin/security", "/admin/applications", "/admin/analytics"];

const DEMO_NAV_BY_ROLE = {
  investor:   DEMO_NAV_INVESTOR,
  buyer:      DEMO_NAV_BUYER,
  importer:   DEMO_NAV_BUYER,
  exporter:   DEMO_NAV_EXPORTER,
  supplier:   DEMO_NAV_EXPORTER,
  downstream: DEMO_NAV_DOWNSTREAM,
};

// Header label per role — replaces "Admin" in the demo top bar.
export const ROLE_HEADER_LABEL = {
  investor:   "Investor",
  buyer:      "Buyer",
  importer:   "Buyer",
  exporter:   "Exporter",
  supplier:   "Exporter",
  downstream: "Downstream Buyer",
};

// Dashboard heading + subheading per role — used by AdminOverview.
export const ROLE_DASHBOARD_COPY = {
  investor:   { title: "Operating Infrastructure", sub: "Live operator view of Delta GreenX's controlled trade network." },
  buyer:      { title: "Buyer Dashboard",          sub: "Source verified suppliers, deploy capital, and track every shipment end-to-end." },
  importer:   { title: "Buyer Dashboard",          sub: "Source verified suppliers, deploy capital, and track every shipment end-to-end." },
  exporter:   { title: "Exporter Dashboard",       sub: "Get paid faster, see buyer KYC, and track your shipments through the full chain." },
  supplier:   { title: "Exporter Dashboard",       sub: "Get paid faster, see buyer KYC, and track your shipments through the full chain." },
  downstream: { title: "Downstream Buyer Dashboard", sub: "Full upstream traceability — every supplier, every corridor, every certificate." },
};

// Allowed routes for the curated role view (anything outside this list will
// redirect to /admin to prevent demo-link abuse via URL paste).
function allowedDemoPathsFor(role) {
  const nav = DEMO_NAV_BY_ROLE[(role || "investor").toLowerCase()] || DEMO_NAV_INVESTOR;
  return new Set(nav.map((n) => n.to));
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { setOpen: setTourOpen } = useTour();
  const navigate = useNavigate();
  const location = useLocation();
  const [demoMode, setDemoMode] = useState(false);
  const [fundName, setFundName] = useState("");
  const [via, setVia] = useState("");
  const [copied, setCopied] = useState(false);

  // Demo viewers must not see admin-only routes even via direct URL paste.
  // Re-route them to the dashboard if they land on a blocked path OR a path
  // that's not part of their role's curated view.
  useEffect(() => {
    if (user?.is_demo !== true) return;
    if (DEMO_BLOCKED_PREFIXES.some((p) => location.pathname.startsWith(p))) {
      navigate("/admin", { replace: true });
      return;
    }
    const allowed = allowedDemoPathsFor(user.via_role);
    // Allow exact matches + nested routes only if their parent is allowed.
    const path = location.pathname;
    const parent = path === "/admin" ? "/admin" : ("/" + path.split("/").slice(1, 3).join("/"));
    if (!allowed.has(parent) && !allowed.has(path)) {
      navigate("/admin", { replace: true });
    }
  }, [user, location.pathname, navigate]);

  // Pick the right demo nav based on the applicant role embedded in the
  // demo JWT (defaults to investor for full pitch).
  const demoNav = user?.is_demo
    ? DEMO_NAV_BY_ROLE[(user.via_role || "investor").toLowerCase()] || DEMO_NAV_INVESTOR
    : null;

  // Server truth: only show the demo banner when /api/auth/me reports
  // is_demo=true. localStorage flags are scrubbed proactively by AuthContext
  // when the active session is real-admin, so the banner & read-only badges
  // never bleed across logins.
  useEffect(() => {
    const realDemo = user?.is_demo === true;
    if (!realDemo) {
      // Real admin (or unauth) — clear any sticky markers and bail.
      try {
        localStorage.removeItem("investorDemoMode");
        localStorage.removeItem("investorDemoVia");
      } catch { /* localStorage unavailable */ }
      setDemoMode(false);
      setVia("");
      setFundName("");
      return;
    }
    const viaId = localStorage.getItem("investorDemoVia") || "";
    setDemoMode(true);
    setVia(viaId);
    analytics.demoEntered("admin", viaId || null);
    if (viaId) {
      axios.get(`${API}/public/investor-link/${encodeURIComponent(viaId)}`)
        .then((r) => { if (r.data?.ok) setFundName(r.data.fund_name); }).catch(() => {});
    }
    if (localStorage.getItem("investorDemoAutoTour") === "1") {
      localStorage.removeItem("investorDemoAutoTour");
      setTimeout(() => setTourOpen(true), 600);
    }
  }, [user, setTourOpen]);

  const handleLogout = async () => {
    localStorage.removeItem("investorDemoMode");
    localStorage.removeItem("investorDemoAutoTour");
    localStorage.removeItem("investorDemoVia");
    await logout();
    navigate("/admin/login");
  };

  const exitDemo = () => {
    analytics.demoExited();
    localStorage.removeItem("investorDemoMode");
    localStorage.removeItem("investorDemoAutoTour");
    localStorage.removeItem("investorDemoVia");
    setDemoMode(false);
    navigate("/");
  };

  const copyDemoLink = () => {
    const tk = process.env.REACT_APP_PUBLIC_DEMO_TOKEN || "dgx-investor-2026";
    const url = `${window.location.origin}/investor-demo?token=${tk}${via ? `&via=${via}` : ""}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className={`min-h-screen bg-[#050A07] ${demoMode ? "demo-readonly" : ""}`} data-testid="admin-layout" data-demo={demoMode}>
      {demoMode && (
        <div className="bg-[#22C55E] text-[#050A07] px-6 py-2 flex items-center justify-between gap-4 text-xs font-semibold tracking-wider uppercase" data-testid="investor-demo-banner">
          <div className="flex items-center gap-2">
            <Eye className="w-3.5 h-3.5" />
            {fundName ? `Welcome, ${fundName} · Read-Only Demo · 30-min` : "Investor Demo Mode · Read-Only · 30-min session"}
            <span className="hidden md:inline opacity-70 normal-case font-normal tracking-normal">
              — operator controls disabled · applicant PII redacted
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setTourOpen(true)} className="hover:underline flex items-center gap-1" data-testid="demo-start-tour">
              <Sparkles className="w-3 h-3" /> Guided Tour
            </button>
            <span className="opacity-40">·</span>
            <button onClick={copyDemoLink} className="hover:underline flex items-center gap-1" data-testid="demo-copy-link">
              {copied ? <><CheckCircle className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Share this demo</>}
            </button>
            <span className="opacity-40">·</span>
            <button onClick={exitDemo} className="hover:underline" data-testid="demo-exit">Exit</button>
          </div>
        </div>
      )}
      <header className="border-b border-[#1A2E22] bg-[#050A07]/90 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" aria-label="Delta GreenX home">
              <BrandLogo as="div" size="md" />
            </a>
            <span className="text-[#1A2E22]">|</span>
            <span className="text-[#A1B2A8] text-sm" data-testid="header-role-label">{ROLE_HEADER_LABEL[(user?.via_role || "").toLowerCase()] || "Admin"}</span>
          </div>
          <div className="flex items-center gap-4">
            {!demoMode && (
              <button
                onClick={() => setTourOpen(true)}
                className="hidden sm:flex items-center gap-2 px-3 py-1.5 border border-[#22C55E]/40 bg-[#22C55E]/5 text-[#22C55E] hover:bg-[#22C55E]/15 transition-colors text-xs font-medium uppercase tracking-wider"
                data-testid="start-investor-tour-btn"
              >
                <Sparkles className="w-3.5 h-3.5" /> Investor Mode
              </button>
            )}
            <span className="text-[#A1B2A8] text-sm hidden sm:inline">{user?.email}</span>
            {!demoMode && (
              <button onClick={handleLogout} className="text-[#A1B2A8] hover:text-white transition-colors flex items-center gap-2 text-sm" data-testid="admin-logout-btn">
                <LogOut className="w-4 h-4" /> Logout
              </button>
            )}
          </div>
        </div>
      </header>
      <nav className="border-b border-[#1A2E22] bg-[#09120D]">
        <div className="max-w-[1400px] mx-auto px-6 flex items-center gap-1 overflow-x-auto">
          {(demoMode ? (demoNav || DEMO_NAV_INVESTOR) : ADMIN_NAV).map(({ to, icon: Icon, label, end }) => (
            <NavLink key={to} to={to} end={end}
              className={({ isActive }) => `flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${isActive ? "border-[#22C55E] text-[#22C55E]" : "border-transparent text-[#A1B2A8] hover:text-white"}`}
              data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
            ><Icon className="w-4 h-4" />{label}</NavLink>
          ))}
        </div>
      </nav>
      <main className="max-w-[1400px] mx-auto px-6 py-8"><Outlet /></main>
    </div>
  );
}
